<template>
  <div>
    <button class="btn btn-green" @click="showModal">+ New staff member</button>

    <!-- Modal -->
    <div class="modal fade" ref="staffMemberModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">New staff member</h4>
            <button type="button" class="close" @click="hideModal" aria-label="Close">
              <span aria-hidden="true">X</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="row d-flex align-items-stretch">
              <!-- Contract details Card -->
              <div class="col-md-6 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <div class="form-group">
                      <h5 class="section-title">Contract details</h5>
                      <label for="first-name">First name</label>
                      <input type="text" id="first-name" class="form-control" v-model="form.firstName" />
                      <span v-if="errors.firstName" class="text-danger">{{ errors.firstName }}</span>
                    </div>
                    <div class="form-group">
                      <label for="last-name">Last name</label>
                      <input type="text" id="last-name" class="form-control" v-model="form.lastName" />
                      <span v-if="errors.lastName" class="text-danger">{{ errors.lastName }}</span>
                    </div>
                    <div class="form-group">
                      <label for="username">Username</label>
                      <input type="text" id="username" class="form-control" v-model="form.username" />
                      <span v-if="errors.username" class="text-danger">{{ errors.username }}</span>
                    </div>
                    <div class="form-group">
                      <label for="password">Password</label>
                      <div class="input-group">
                        <input :type="passwordFieldType" id="password" class="form-control" v-model="form.password" />
                        <span v-if="errors.password" class="text-danger">{{ errors.password }}</span>
                        <button class="btn btn-outline-primary" type="button" @click="togglePasswordVisibility">
                          <i :class="passwordFieldType === 'password'  ? 'fa fa-eye' : 'fa fa-eye-slash'" class="f-18"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Access Card -->
              <div class="col-md-6 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <div class="form-group">
                      <h5 class="section-title">Access</h5>
                      <label for="role">Role</label>
                      <select id="role" class="form-control" v-model="form.role_id">
                        <option value="" disabled>Select a role</option>
                        <option v-for="role in roleStore.roles" :key="role.id" :value="role.id">
                          {{ role.name }}
                        </option>
                      </select>
                      <span v-if="errors.role_id" class="text-danger">{{ errors.role_id }}</span>
                    </div>
                    <div class="form-group mt-4">
                      <div class="col-12 mt-2 checkbox checkbox-success">
                        <input id="accessEnabled" type="checkbox" v-model="form.enabled">
                        <label for="accessEnabled">Enabled</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end">
                <button class="btn btn-green btn-ok me-1" @click="submitForm">OK</button>
                <button class="btn btn-cancel" @click="hideModal">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Modal } from 'bootstrap';
import { useMemberStore } from '@/store/admin/member';
import { useRoleStore } from '@/store/admin/role';

const memberStore = useMemberStore();
const roleStore = useRoleStore();

// 表單的初始狀態
const form = ref({
  firstName: '',
  lastName: '',
  username: '',
  password: '', // 預設密碼
  role_id: '',
  enabled: false,
});

const errors = ref({
  firstName: '',
  lastName: '',
  username: '',
  password: '',
  role_id: '',
});

const staffMemberModal = ref(null);
let modalInstance = null;

// 密碼顯示與隱藏控制
const passwordFieldType = ref('password');
const togglePasswordVisibility = () => {
  passwordFieldType.value = passwordFieldType.value === 'password' ? 'text' : 'password';
};

const showModal = () => {
  if (!modalInstance) {
    modalInstance = new Modal(staffMemberModal.value);
  }
  modalInstance.show();
};

const hideModal = () => {
  if (modalInstance) {
    modalInstance.hide();
  }
};

const validateForm = () => {
  let valid = true;

  // 驗證每個欄位
  if (!form.value.firstName) {
    errors.value.firstName = 'First name is required';
    valid = false;
  } else {
    errors.value.firstName = '';
  }

  if (!form.value.lastName) {
    errors.value.lastName = 'Last name is required';
    valid = false;
  } else {
    errors.value.lastName = '';
  }

  if (!form.value.username) {
    errors.value.username = 'Username is required';
    valid = false;
  } else {
    errors.value.username = '';
  }

  if (!form.value.password || form.value.password.length < 6) {
    errors.value.password = 'Password must be at least 6 characters';
    valid = false;
  } else {
    errors.value.password = '';
  }

  if (!form.value.role_id) {
    errors.value.role_id = 'Role is required';
    valid = false;
  } else {
    errors.value.role_id = '';
  }

  return valid;
};

const submitForm = () => {
  if (!validateForm()) {
    return;
  }

  // 準備資料
  const payload = {
    first_name: form.value.firstName,
    middle_name: form.value.middleName,
    last_name: form.value.lastName,
    username: form.value.username,
    password: form.value.password,
    role_id: form.value.role_id,
    enabled: form.value.enabled ? 1 : 0,
  };

  memberStore.createMember(payload)
    .then(() => {
      hideModal();
      // 初始化表單
      form.value = {
        firstName: '',
        middleName: '',
        lastName: '',
        username: '',
        password: '',
        role_id: '',
        enabled: false,
      };
    })
    .catch(error => {
      console.error('Error creating member:', error);
    });
};

// 在組件掛載時獲取角色列表
onMounted(async () => {
  await roleStore.fetchRoles();
});
</script>

<style scoped>
label {
  font-size: 16px;
  margin-top: 5px;
}

.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 244.83px;
  height: 46px;
  border-radius: 30px;
  padding: 10px, 32px, 10px, 32px;
}

.btn-green:hover {
  background-color: #00b452;
}

.btn-ok {
  width: 150px;
}

.btn-cancel {
  background-color: #fff;
  color: #8476f4;
  border: 2px solid #8476f4;
  border-radius: 30px;
  width: 150px;
  height: 46px;
  padding: 14px 32px 14px 32px;
}

.btn-cancel:hover {
  background-color: #e6e6ff;
}

.modal-content {
  border-radius: 10px;
  padding: 20px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header {
  border-bottom: 1px solid #dee2e6;
  font-weight: bold;
  background-color: #d9dde3;
}

.card {
  background-color: #d9dde3;
}

.close {
  color: #333;
  opacity: 1;
  font-size: 20px;
  padding: 0;
  background: none;
  border: none;
}

.close:hover {
  color: #000;
}

/* 使用 flexbox 來讓卡片等高 */
.row.d-flex {
  display: flex;
}

.col-md-6.d-flex {
  display: flex;
  flex-direction: column;
}

.card.flex-grow-1 {
  flex-grow: 1;
}

.align-items-stretch {
  align-items: stretch;
}

.text-danger {
  color: red;
  font-size: 0.9em;
}
</style>
